import {Outlet, useLocation, useMatches} from "react-router";
import {useCallback, useEffect, useLayoutEffect, useMemo, useState} from "react";
import {useQuery} from "@tanstack/react-query";
import "font-awesome/css/font-awesome.min.css";
import "../../css/style-menu.css";
import Sidebar from "./components/sidebar.jsx";
import SearchMenuModal from "./components/search-menu-modal.jsx";
import useAppMenu from "./hooks/use-app-menu.js";
import {findMenuItem, resolveTo} from "./utils/menu-utils.js";
import {recordActivity} from "./hooks/use-recent-activity.js";
import {AppShellContext} from "./app-shell-context.js";
import useKeepSession from "../../hooks/use-keep-session.js";
import ReportsService from "../reports/reports/reports-service.js";

const normalizeReportPath = (path) => String(path ?? "").replace(/^\/+|\/+$/g, "");

const getReportPathBase = (path) => normalizeReportPath(path)
    .split("/")
    .filter((segment) => segment && !segment.startsWith(":"))
    .join("/");

const findActiveReportTitle = (reports, pathname) => {
    const currentPath = normalizeReportPath(pathname).replace(/^reports\/?/, "");

    return reports.find((report) => {
        const reportPath = getReportPathBase(report?.path);
        return reportPath && (currentPath === reportPath || currentPath.startsWith(`${reportPath}/`));
    })?.name;
};

export const resolveDocumentTitle = ({pageTitle, reportTitle, activeItem}) => (
    pageTitle || reportTitle || activeItem?.homeName || activeItem?.name || "Sentry"
);

const AppShell = () => {
    const {pathname} = useLocation();
    const matches = useMatches();
    const {menu, shortcuts} = useAppMenu();
    const {messageCount, notificationCount} = useKeepSession();
    const isReportDetailPath = pathname.startsWith("/reports/");

    const {data: reports = []} = useQuery({
        queryKey: ["reports"],
        queryFn: ReportsService.list,
        enabled: isReportDetailPath,
        staleTime: 5 * 60 * 1000,
    });

    const activeItem = useMemo(
        () => findMenuItem(menu, pathname) ?? findMenuItem(shortcuts, pathname),
        [menu, shortcuts, pathname],
    );

    const reportTitle = useMemo(
        () => isReportDetailPath ? findActiveReportTitle(reports, pathname) : null,
        [isReportDetailPath, reports, pathname],
    );

    const routeHidesMenu = matches.some((match) => match.handle?.hideMenu);
    const [collapsed, setCollapsed] = useState(() => !!activeItem?.hideMenu || routeHidesMenu);
    const [pageTitle, setPageTitleState] = useState(null);
    const [searchOpen, setSearchOpen] = useState(false);
    const currentPageTitle = pageTitle?.pathname === pathname ? pageTitle.title : null;

    useLayoutEffect(() => {
        setCollapsed(!!activeItem?.hideMenu || routeHidesMenu);
    }, [activeItem]);

    useLayoutEffect(() => {
        document.title = resolveDocumentTitle({pageTitle: currentPageTitle, reportTitle, activeItem});
        if (activeItem) recordActivity({title: activeItem.name, to: resolveTo(activeItem.url)});
    }, [activeItem, currentPageTitle, reportTitle, routeHidesMenu]);

    useLayoutEffect(() => {
        const pageWrapper = document.getElementById("page-wrapper");
        if (pageWrapper) pageWrapper.scrollTop = 0;
    }, [pathname]);

    useEffect(() => {
        const handler = (e) => {
            if (e.altKey && (e.key === "2" || e.code === "Digit2")) {
                e.preventDefault();
                setSearchOpen(true);
            }
        };
        window.addEventListener("keydown", handler);
        return () => window.removeEventListener("keydown", handler);
    }, []);

    const toggle = useCallback(() => setCollapsed((prev) => !prev), []);
    const setPageTitle = useCallback((title) => {
        setPageTitleState({pathname, title});
    }, [pathname]);

    const shell = useMemo(() => ({collapsed, toggle, setPageTitle}), [collapsed, toggle, setPageTitle]);

    const badges = useMemo(
        () => ({
            "user-messages": messageCount,
            "notifications": notificationCount,
            "notification-open": notificationCount,
        }),
        [messageCount, notificationCount],
    );

    return (
        <AppShellContext.Provider value={shell}>
            <Sidebar
                menu={menu}
                shortcuts={shortcuts}
                collapsed={collapsed}
                onToggle={toggle}
                onOpenSearch={() => setSearchOpen(true)}
                badges={badges}
            />

            <SearchMenuModal open={searchOpen} setOpen={setSearchOpen} />

            <div
                id="page-wrapper"
                style={{
                    height: "100vh",
                    overflowY: "auto",
                    marginLeft: collapsed ? 0 : "220px",
                }}
            >
                <Outlet context={{activeItem, collapsed}} key={pathname}/>
            </div>
        </AppShellContext.Provider>
    );
};

export default AppShell;